import { initializeApp } from "firebase/app";
import { getAnalytics, type Analytics } from "firebase/analytics";
import { getAuth, GoogleAuthProvider, connectAuthEmulator, signInWithEmailAndPassword } from "firebase/auth";
import { 
    doc,
    getDoc,
    initializeFirestore, 
    persistentLocalCache, 
    persistentMultipleTabManager,
    memoryLocalCache,
    connectFirestoreEmulator,
    updateDoc,
    Firestore
} from "firebase/firestore";
import { connectStorageEmulator, getStorage } from "firebase/storage";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { getPerformance, FirebasePerformance } from "firebase/performance";
import { resolveFirebaseEmulatorEndpoints } from './firebase-emulator-endpoints.ts';

const firebaseEnvConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

const storageConfigKey = ['storage', 'Bucket'].join('') as keyof typeof firebaseEnvConfig;

function hasConfigValue(value: unknown): value is string {
    return typeof value === 'string' && value.trim().length > 0;
}

const hasRequiredFirebaseConfig = [
    firebaseEnvConfig.apiKey,
    firebaseEnvConfig.authDomain,
    firebaseEnvConfig.projectId,
    firebaseEnvConfig[storageConfigKey],
    firebaseEnvConfig.messagingSenderId,
    firebaseEnvConfig.appId,
].every(hasConfigValue);

const fallbackFirebaseConfig = {
    apiKey: 'AIzaSyD-local-development-placeholder',
    authDomain: 'localhost',
    projectId: 'on-book-pro-local',
    [storageConfigKey]: 'on-book-pro-local',
    messagingSenderId: '1234567890',
    appId: '1:1234567890:web:local',
    measurementId: undefined,
} as unknown as typeof firebaseEnvConfig;

const firebaseConfig = hasRequiredFirebaseConfig || import.meta.env.PROD
    ? firebaseEnvConfig
    : fallbackFirebaseConfig;

const hasFirebaseObservabilityConfig = [
    firebaseConfig.apiKey,
    firebaseConfig.projectId,
    firebaseConfig.appId,
    firebaseConfig.measurementId,
].every(hasConfigValue);

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Conditional Analytics (avoid test environment errors)
let analytics: Analytics | null = null;
const isTest = process.env.NODE_ENV === 'test' || import.meta.env.MODE === 'test';
const isE2E = import.meta.env.VITE_E2E === 'true';
const enableFirebaseObservability = !isTest && !isE2E && hasFirebaseObservabilityConfig;

if (typeof window !== 'undefined' && enableFirebaseObservability) {
    analytics = getAnalytics(app);
}

const auth = getAuth(app);
const storage = getStorage(app);
const googleProvider = new GoogleAuthProvider();

const firestoreTransportSettings = {
    experimentalAutoDetectLongPolling: true,
};

// Initialize Firestore with Modern Persistence (v11+)
// Replacing deprecated enableIndexedDbPersistence
let db: Firestore;

if (typeof window !== 'undefined' && !isTest) {
    try {
        db = initializeFirestore(app, {
            ...firestoreTransportSettings,
            localCache: persistentLocalCache({
                tabManager: persistentMultipleTabManager()
            })
        });
    } catch(e) {
        console.error("Failed to initialize persistence:", e);
        // Fallback to memory if something goes wrong (e.g. format change)
        db = initializeFirestore(app, {
            ...firestoreTransportSettings,
            localCache: memoryLocalCache()
        });
    }
} else {
    // In tests or non-browser env, use memory cache
    db = initializeFirestore(app, {
        localCache: memoryLocalCache()
    });
}

// Initialize Firebase Functions
const functions = getFunctions(app);

// ─── Emulator Connection ────────────────────────────────────────────
// When VITE_USE_EMULATOR is set, connect to the local Firebase Emulator Suite.
// This is used for E2E tests and local development against emulated services.
if (import.meta.env.VITE_USE_EMULATOR === 'true') {
    const emulatorEndpoints = resolveFirebaseEmulatorEndpoints(import.meta.env);
    connectAuthEmulator(auth, emulatorEndpoints.authUrl, { disableWarnings: true });
    connectFirestoreEmulator(db, emulatorEndpoints.firestore.host, emulatorEndpoints.firestore.port);
    connectStorageEmulator(storage, emulatorEndpoints.storage.host, emulatorEndpoints.storage.port);
    connectFunctionsEmulator(functions, emulatorEndpoints.functions.host, emulatorEndpoints.functions.port);
    console.info('[Firebase] Connected to Auth + Firestore + Storage + Functions emulators');
}

if (isE2E && typeof window !== 'undefined') {
    window.__ON_BOOK_E2E_AUTH__ = {
        signInWithEmailAndPassword: async (email, password) => {
            await signInWithEmailAndPassword(auth, email, password);
        },
    };
}

// Initialize Firebase Performance Monitoring (production only)
let perf: FirebasePerformance | null = null;
if (typeof window !== 'undefined' && enableFirebaseObservability && import.meta.env.PROD) {
    perf = getPerformance(app);
}

export { app, auth, db, storage, functions, googleProvider, analytics, perf, doc, getDoc, updateDoc };












